import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { Stack } from '@mui/material';

import { MetricCardProps } from '@occc/ui-design-system';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';
import { RetailerNetworkTobaccoProgramMetricType } from '@occc/graphql';
import { hasAnyNonZeroProps } from '@occc/utils';

import { RetailerNetworkTobaccoPerformance } from './retailer-network-tobacco-performance.component';
import { RetailerNetworkTobaccoPerformanceManufacturers } from './retailer-network-tobacco-performance-manufacturers.components';

import { RetailerNetworkDataLoading } from '../../components/retailer-network-data-loading.component';
import { routeCreator } from '../../retailer-connect.utils';
import { useRetailerNetworkTobaccoProgramMetricsOverview } from '../../hooks/use-retailer-network-tobacco-program-metrics-overview.hook';
import { useRetailerNetworkCpgManufacturerPrograms } from '../../hooks/use-retailer-network-cpg-manufacturer-programs.hook';
import { RetailerNetworkDataPlaceholder } from '../../components/retailer-network-data-placeholder.component';
import { tobaccoEmptyDataDescription, tobaccoEmptyDataTitle } from '../../retailer-connect.copies';

type RetailerNetworkTobaccoPerformanceControllerProps = {
  retailerNetworkUuid: string;
  startDate: Date;
  endDate: Date;
  onZeroData?: () => void;
};

export const RetailerNetworkTobaccoPerformanceController = ({
  retailerNetworkUuid,
  startDate,
  endDate,
  onZeroData,
}: RetailerNetworkTobaccoPerformanceControllerProps) => {
  const navigate = useNavigate();

  const { data, error, loading } = useRetailerNetworkTobaccoProgramMetricsOverview({
    retailerNetworkUuidEquals: retailerNetworkUuid,
    dateFrom: formatDateTime(startDate, DateTimeFormat.DateDashed),
    dateTo: formatDateTime(endDate, DateTimeFormat.DateDashed),
  });

  const {
    data: manufacturersPrograms,
    error: manufacturersProgramsError,
    loading: manufacturersProgramsLoading,
  } = useRetailerNetworkCpgManufacturerPrograms({
    retailerNetworkUuidEquals: retailerNetworkUuid,
    dateFrom: startDate.toISOString(),
    dateTo: endDate.toISOString(),
  });

  const onMetricClick = useCallback(
    (metricCard: MetricCardProps<RetailerNetworkTobaccoProgramMetricType>) => {
      navigate(
        routeCreator.tobaccoPerformanceDetails(retailerNetworkUuid, {
          metricKey: metricCard.payload,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }),
      );
    },
    [navigate, retailerNetworkUuid, endDate, startDate],
  );

  const metrics = useMemo(() => {
    return (
      data?.retailerNetworkTobaccoProgramMetricsOverview?.tobaccoTransactionsTimeSeries.map(
        ({ date, value }) => ({
          date: parseDate(date),
          value,
        }),
      ) ?? []
    );
  }, [data?.retailerNetworkTobaccoProgramMetricsOverview?.tobaccoTransactionsTimeSeries]);

  const isZeroData = useMemo(
    () =>
      data?.retailerNetworkTobaccoProgramMetricsOverview &&
      !hasAnyNonZeroProps(data.retailerNetworkTobaccoProgramMetricsOverview),
    [data?.retailerNetworkTobaccoProgramMetricsOverview],
  );

  useEffect(() => {
    if (!isZeroData || !onZeroData) return;
    onZeroData();
  }, [isZeroData, onZeroData]);

  return (
    <RetailerNetworkDataLoading
      isLoading={loading || manufacturersProgramsLoading}
      hasData={!!data && !!manufacturersPrograms}
      hasError={!!error || !!manufacturersProgramsError}
    >
      {isZeroData ? (
        <RetailerNetworkDataPlaceholder
          title={tobaccoEmptyDataTitle}
          description={tobaccoEmptyDataDescription}
        />
      ) : (
        !!data && (
          <Stack gap={1} mb={8}>
            <RetailerNetworkTobaccoPerformance
              metrics={metrics}
              onMetricClick={onMetricClick}
              performance={data.retailerNetworkTobaccoProgramMetricsOverview.performance}
              previousPerformance={
                data.retailerNetworkTobaccoProgramMetricsOverview.previousPerformance
              }
              totalTransactions={
                data.retailerNetworkTobaccoProgramMetricsOverview.performance.totalTransactions
              }
              prevTotalTransactions={
                data.retailerNetworkTobaccoProgramMetricsOverview.previousPerformance
                  .totalTransactions
              }
            />
            <RetailerNetworkTobaccoPerformanceManufacturers
              startDate={startDate}
              endDate={endDate}
              retailerNetworkUuid={retailerNetworkUuid}
              manufacturerPrograms={manufacturersPrograms?.retailerNetworkCPGManufacturerPrograms}
            />
          </Stack>
        )
      )}
    </RetailerNetworkDataLoading>
  );
};
